import { fmtClock, fmtDuration, useNow } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { initials } from "@/lib/format";

export function LiveDuration({ from, clock = false, className, ...props }) {
  const now = useNow();
  const seconds = from ? (now - new Date(from).getTime()) / 1000 : 0;
  return (
    <span className={cn("mono tabular-nums", className)} {...props}>
      {clock ? fmtClock(seconds) : fmtDuration(seconds)}
    </span>
  );
}

export function AgeBadge({ seconds, warnHours = 24, criticalHours = 72, className }) {
  const h = seconds / 3600;
  const tone = h >= criticalHours ? "text-red-500" : h >= warnHours ? "text-amber-500" : "text-muted-foreground";
  return <span className={cn("mono text-[11px] tabular-nums", tone, className)}>{fmtDuration(seconds)}</span>;
}

export function UserAvatar({ name, src, size = "md", className }) {
  const sizes = { xs: "h-5 w-5 text-[9px]", sm: "h-6 w-6 text-[10px]", md: "h-8 w-8 text-xs", lg: "h-12 w-12 text-sm" };
  return (
    <Avatar className={cn("rounded-none border border-border", sizes[size], className)}>
      {src && <AvatarImage src={src} alt={name} className="object-cover" />}
      <AvatarFallback className="rounded-none bg-muted mono font-semibold">{initials(name)}</AvatarFallback>
    </Avatar>
  );
}

export function PageHeader({ eyebrow, title, description, actions, children }) {
  return (
    <div className="flex flex-col gap-3 border-b border-border px-5 py-4 md:flex-row md:items-end md:justify-between">
      <div>
        {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>}
        {children}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ title, hint, icon: Icon, action, testId }) {
  return (
    <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 p-8 text-center" data-testid={testId}>
      {Icon && <Icon className="mb-2 h-8 w-8 text-muted-foreground/60" strokeWidth={1.5} />}
      <div className="text-sm font-semibold">{title}</div>
      {hint && <div className="max-w-sm text-xs text-muted-foreground">{hint}</div>}
      {action}
    </div>
  );
}

export function Stat({ label, value, tone, testId, sub }) {
  return (
    <div className="surface px-4 py-3" data-testid={testId}>
      <div className="eyebrow">{label}</div>
      <div className={cn("mono mt-1 text-2xl font-semibold tabular-nums", tone)}>{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function Field({ label, hint, children, className }) {
  return (
    <label className={cn("block space-y-1.5", className)}>
      {label && <span className="eyebrow block">{label}</span>}
      {children}
      {hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}
